import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import reviewsData from '../reviews.json';
import logoImage from '../assets/logo.png';

const ReviewsSection = styled.section`
  padding: 100px 0;
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  text-align: center;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const ReviewsTitle = styled.h4`
  font-size: 2.5rem;
  font-weight: 700;
  color: var(--primary-blue);
  text-align: center;
  margin-bottom: 2rem;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 50%;
    transform: translateX(-50%);
    width: 60px;
    height: 4px;
    background: var(--primary-orange);
    border-radius: 2px;
  }
  
  @media (max-width: 768px) {
    font-size: 2rem;
    
    &::after {
      left: 50%;
      transform: translateX(-50%);
    }
  }
`;

const ReviewsDescription = styled.p`
  font-size: 1.2rem;
  color: #666;
  margin-bottom: 3rem;
`;

const ReviewsButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 2rem;
  margin-bottom: 3rem;
  flex-wrap: wrap;
`;

const ReviewsButtonWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const ReviewButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 1rem 2rem;
  border-radius: 50px;
  font-weight: 600;
  text-decoration: none;
  font-size: 1.1rem;
  transition: all 0.3s ease;
  text-transform: uppercase;
  letter-spacing: 1px;
  
  &.btn-google-reviews {
    background: linear-gradient(45deg, #4285f4, #34a853);
    color: white;
    
    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 25px rgba(66, 133, 244, 0.3);
    }
  }
  
  &.btn-facebook-reviews {
    background: linear-gradient(45deg, #1877f2, #42a5f5);
    color: white;
    
    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 25px rgba(24, 119, 242, 0.3);
    }
  }
  
  i {
    font-size: 1.2rem;
  }
`;

const SocialMediaContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 1.5rem;
  flex-wrap: wrap;
`;

const SocialMediaButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 60px;
  height: 60px;
  border-radius: 50%;
  color: white;
  font-size: 1.5rem;
  text-decoration: none;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-3px) scale(1.1);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.2);
  }
  
  &.instagram {
    background: linear-gradient(45deg, #f09433, #e6683c, #dc2743, #cc2366, #bc1888);
  }
  
  &.facebook {
    background: linear-gradient(45deg, #1877f2, #42a5f5);
  }
  
  &.google {
    background: linear-gradient(45deg, #4285f4, #34a853);
  }
`;

const ReviewsCarousel = styled.div`
  display: flex;
  gap: 2rem;
  margin: 3rem 0;
  overflow: hidden;
  padding: 1rem 0;
  position: relative;
  width: 100%;
`;

const ScrollingContainer = styled.div`
  display: flex;
  gap: 2rem;
  animation: infiniteScroll 60s linear infinite;
  
  @keyframes infiniteScroll {
    0% {
      transform: translateX(0);
    }
    100% {
      transform: translateX(calc(-100% / 3));
    }
  }
  
  /* Pause animation on hover */
  &:hover {
    animation-play-state: paused;
  }
`;

const ReviewCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 1.25rem;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  position: relative;
  height: fit-content;
  max-width: 417px;
  flex-shrink: 0;
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 6px 24px rgba(0, 0, 0, 0.12);
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(45deg, #ec5c2c, #ff7043);
    border-radius: 12px 12px 0 0;
  }
`;

const ReviewHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1rem;
`;

const ProfileImage = styled.img`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid #f0f0f0;
  flex-shrink: 0;
`;

const ReviewerInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const ReviewerName = styled.h5`
  margin: 0;
  font-weight: 600;
  color: #333;
  font-size: 1rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ReviewTime = styled.p`
  margin: 0;
  color: #666;
  font-size: 0.85rem;
  margin-top: 0.25rem;
`;

const StarRating = styled.div`
  display: flex;
  gap: 0.15rem;
  margin-bottom: 0.75rem;
`;

const Star = styled.span`
  color: #ffc107;
  font-size: 1rem;
`;

const ReviewText = styled.p`
  color: #555;
  line-height: 1.5;
  font-size: 0.95rem;
  margin: 0;
  display: -webkit-box;
  -webkit-line-clamp: 4;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Reviews: React.FC = () => {
  const { t } = useTranslation();
  
  const renderStars = (rating: number) => {
    return Array.from({ length: 5 }, (_, index) => (
      <Star key={index}>
        {index < rating ? '★' : '☆'}
      </Star>
    ));
  };

  return (
    <ReviewsSection>
      <Container>
        <ReviewsTitle>{t('reviews.title')}</ReviewsTitle>
        <ReviewsDescription>{t('reviews.description')}</ReviewsDescription>
        
        <ReviewsCarousel>
          <ScrollingContainer>
            {[...reviewsData, ...reviewsData, ...reviewsData].map((review, index) => (
              <ReviewCard key={index}>
                <ReviewHeader>
                  <ProfileImage 
                    src={review.profile_photo_url} 
                    alt={review.author_name}
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.src = logoImage;
                    }}
                  />
                  <ReviewerInfo>
                    <ReviewerName>{review.author_name}</ReviewerName>
                    <ReviewTime>{review.relative_time_description}</ReviewTime>
                  </ReviewerInfo>
                </ReviewHeader>
                
                <StarRating>
                  {renderStars(review.rating)}
                </StarRating>
                
                <ReviewText>{review.text}</ReviewText>
              </ReviewCard>
            ))}
          </ScrollingContainer>
        </ReviewsCarousel>
        
        <ReviewsButtonContainer className="row justify-content-center reviews-btn-container">
          <ReviewsButtonWrapper className="reviews-btn-wrapper">
            <ReviewButton 
              className="btn-google-reviews" 
              target="_blank"
              rel="noreferrer"
              href="https://www.google.com/search?q=google+reviews+universal+drive&oq=google+reviews+universal+drive&aqs=chrome..69i57j69i64.7036j0j7&sourceid=chrome&ie=UTF-8#lrd=0x47c5e2728776ace3:0xfb40dcca38e8a162,1,,,"
            >
              <img src="/assets/google-logo.svg" alt="Google" style={{width: '20px', height: '20px', marginRight: '8px'}} />{t('reviews.google')}
            </ReviewButton>
          </ReviewsButtonWrapper>
          <ReviewsButtonWrapper className="reviews-btn-wrapper">
            <ReviewButton 
              className="btn-facebook-reviews" 
              target="_blank"
              rel="noreferrer"
              href="https://www.facebook.com/pg/universaldrive/reviews/?ref=page_internal"
            >
              <img src="/assets/facebook-logo.svg" alt="Facebook" style={{width: '20px', height: '20px', marginRight: '8px', filter: 'brightness(0) invert(1)'}} />{t('reviews.facebook')}
            </ReviewButton>
          </ReviewsButtonWrapper>
        </ReviewsButtonContainer>
        
        <SocialMediaContainer className="row justify-content-center social-media-container">
          <SocialMediaButton 
            className="social-media-btn-main instagram" 
            target="_blank" 
            rel="noreferrer"
            href="https://www.instagram.com/universaldrive/"
          >
            <img src="/assets/instagram-logo.svg" alt="Instagram" style={{width: '24px', height: '24px', filter: 'brightness(0) invert(1)'}} />
          </SocialMediaButton>
          <SocialMediaButton 
            className="social-media-btn-main facebook" 
            target="_blank" 
            rel="noreferrer"
            href="https://www.facebook.com/universaldrive/"
          >
            <img src="/assets/facebook-logo.svg" alt="Facebook" style={{width: '24px', height: '24px', filter: 'brightness(0) invert(1)'}} />
          </SocialMediaButton>
          <SocialMediaButton 
            className="social-media-btn-main google" 
            target="_blank"
            rel="noreferrer"
            href="https://www.google.com/search?q=google+reviews+universal+drive&oq=google+reviews+universal+drive&aqs=chrome..69i57j69i64.7036j0j7&sourceid=chrome&ie=UTF-8#lrd=0x47c5e2728776ace3:0xfb40dcca38e8a162,1,,,"
          >
            <img src="/assets/google-logo.svg" alt="Google" style={{width: '24px', height: '24px', filter: 'brightness(0) invert(1)'}} />
          </SocialMediaButton>
        </SocialMediaContainer>
      </Container>
    </ReviewsSection>
  );
};

export default Reviews;